import { useCallback, useEffect, useRef, useState } from "react";
import {
  listQcChecklist,
  listQcChecklistChecks,
  listQcSessionSections,
  submitQcSessionSection,
  type QcChecklistCategory,
  type QcChecklistCheck,
  type QcChecklistItem,
  type QcSessionSection,
} from "@/lib/manufacturing/builderAssistantChecklistClient";
import { startQcCorrection } from "@/lib/manufacturing/qcDashboardClient";
import type { BuilderAssistantObservation } from "@/lib/manufacturing/builderAssistantTypes";
import { flushQcChecklistChecks, type PendingQcCheck } from "./flushQcChecklistChecks";
import { settleQcPhotos } from "./useQcPhotoQueue";
import { useQcChecklistAutosave } from "./useQcChecklistAutosave";
import { useQcPhotoQueue } from "./useQcPhotoQueue";

const WALKING = new Set(["active", "tentative", "archive_requested"]);

function mergeChecks(
  current: readonly QcChecklistCheck[],
  marks: Record<string, PendingQcCheck>,
  saved: { item_id: string; observation_id: string | null }[],
): QcChecklistCheck[] {
  const observationByItem = new Map(saved.map((row) => [row.item_id, row.observation_id]));
  const next = current.map((check) => {
    const mark = marks[check.item_id];
    if (!mark) return check;
    const observationId = observationByItem.has(check.item_id) ? observationByItem.get(check.item_id) ?? null : check.observation_id;
    return { ...check, result: mark.result, note: mark.note, observation_id: observationId };
  });
  for (const [itemId, mark] of Object.entries(marks)) {
    if (next.some((check) => check.item_id === itemId)) continue;
    next.push({
      item_id: itemId,
      result: mark.result,
      note: mark.note,
      observation_id: observationByItem.get(itemId) ?? null,
      pass_url: null,
      has_pass: false,
    });
  }
  return next;
}

export function useQcChecklistWalkModel({
  sessionId,
  observations,
  photographedObservationIds,
  evidenceStamp,
  onChanged,
  onFlushPending,
  onOpenSession,
}: {
  sessionId: string;
  observations: readonly BuilderAssistantObservation[];
  photographedObservationIds: readonly string[];
  evidenceStamp: string;
  onChanged: () => void | Promise<void>;
  onFlushPending?: (flush: () => Promise<{ ok: true } | { ok: false; error: string }>) => void;
  onOpenSession: (sessionId: string) => void;
}) {
  const [categories, setCategories] = useState<QcChecklistCategory[]>([]);
  const [items, setItems] = useState<QcChecklistItem[]>([]);
  const [checks, setChecks] = useState<QcChecklistCheck[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [sectionRows, setSectionRows] = useState<QcSessionSection[]>([]);
  const [failurePhotosRequired, setFailurePhotosRequired] = useState(false);
  const [failureNotesRequired, setFailureNotesRequired] = useState(false);
  const [savedFail, setSavedFail] = useState<Record<string, number>>({});
  const [categoryByObservation, setCategoryByObservation] = useState<Record<string, string>>({});
  const [openSectionId, setOpenSectionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const openedForSession = useRef<string | null>(null);
  const reloadGen = useRef(0);
  const checksRef = useRef(checks);
  checksRef.current = checks;

  const reload = useCallback(async (): Promise<boolean> => {
    const gen = reloadGen.current + 1;
    reloadGen.current = gen;
    const [catalog, sessionChecks, sections] = await Promise.all([
      listQcChecklist(),
      listQcChecklistChecks(sessionId),
      listQcSessionSections(sessionId),
    ]);
    if (gen !== reloadGen.current) return true;
    if (!catalog.ok) {
      setError(catalog.error);
      return false;
    }
    if (!sessionChecks.ok) {
      setError(sessionChecks.error);
      return false;
    }
    if (!sections.ok) {
      setError(sections.error);
      return false;
    }
    setError(null);
    setCategories(catalog.data.categories);
    setItems(catalog.data.items);
    setFailurePhotosRequired(catalog.data.failure_photos_required);
    setFailureNotesRequired(catalog.data.failure_notes_required);
    checksRef.current = sessionChecks.data.checks;
    setChecks(sessionChecks.data.checks);
    setSelected(sections.data.category_ids);
    setSectionRows(sections.data.sections ?? []);
    if (openedForSession.current !== sessionId) {
      openedForSession.current = sessionId;
      const failed = new Set(sessionChecks.data.checks.filter((check) => check.result === "fail").map((check) => check.item_id));
      const categoryId = catalog.data.items.find((item) => failed.has(item.id))?.category_id;
      if (categoryId) setOpenSectionId(categoryId);
    }
    return true;
  }, [sessionId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    setSavedFail({});
  }, [evidenceStamp]);

  const draft = useQcChecklistAutosave({
    sessionId,
    checks,
    onError: setError,
    onSaved: (marks, saved) => {
      const next = mergeChecks(checksRef.current, marks, saved);
      checksRef.current = next;
      setChecks(next);
    },
  });
  const photos = useQcPhotoQueue({
    sessionId,
    ensureSaved: async () => {
      const flushed = await draft.persistDraft();
      if (!flushed.ok) return flushed;
      const ids = new Map(flushed.saved.map((row) => [row.item_id, row.observation_id]));
      return {
        ok: true as const,
        observationId(itemId: string) {
          if (ids.has(itemId)) return ids.get(itemId) ?? null;
          return checksRef.current.find((row) => row.item_id === itemId)?.observation_id ?? null;
        },
      };
    },
    onUploaded: (photo) => {
      if (photo.kind === "pass") {
        const next = checksRef.current.map((row) => (row.item_id === photo.itemId ? { ...row, has_pass: true, result: row.result || "pass" } : row));
        checksRef.current = next;
        setChecks(next);
        return;
      }
      setSavedFail((current) => ({ ...current, [photo.itemId]: (current[photo.itemId] ?? 0) + 1 }));
    },
  });
  const draftRef = useRef(draft);
  const photosRef = useRef(photos);
  draftRef.current = draft;
  photosRef.current = photos;

  useEffect(() => {
    const session = sessionId;
    return () => {
      const marks = draftRef.current.takePending();
      const leftover = photosRef.current.take(session);
      const known = checksRef.current;
      void (async () => {
        const saved = Object.keys(marks).length > 0 ? await flushQcChecklistChecks(session, marks) : { ok: true as const, saved: [] };
        if (!saved.ok || leftover.length === 0) return;
        const ids = new Map(saved.saved.map((row) => [row.item_id, row.observation_id]));
        await settleQcPhotos(leftover, (itemId) => {
          if (ids.has(itemId)) return ids.get(itemId) ?? null;
          return known.find((row) => row.item_id === itemId)?.observation_id ?? null;
        });
      })();
    };
  }, [sessionId]);

  async function flushAll(): Promise<{ ok: true } | { ok: false; error: string }> {
    const marks = await draftRef.current.persistDraft();
    if (!marks.ok) return marks;
    return photosRef.current.drain();
  }

  useEffect(() => {
    onFlushPending?.(() => flushAll());
  }, [onFlushPending, sessionId]);

  const visibleCategories = categories.filter((row) => row.status !== "archived");
  const chosen = new Set(selected);
  const visibleItems = items.filter((item) => WALKING.has(item.status) && chosen.has(item.category_id));
  const linked = new Set([
    ...items.map((item) => item.source_observation_id).filter((id): id is string => Boolean(id)),
    ...checks.map((check) => check.observation_id).filter((id): id is string => Boolean(id)),
  ]);
  const loose = observations.filter((row) => !linked.has(row.id) && row.description.trim());
  const examples = observations.filter((row) => row.observation_type === "CONFORMING_EXAMPLE" || row.observation_type === "REFERENCE_STANDARD");

  function savedFailureCount(itemId: string, observationId: string | null) {
    const fromBundle = observationId ? photographedObservationIds.filter((id) => id === observationId).length : 0;
    return fromBundle + (savedFail[itemId] ?? 0);
  }

  function openFailuresFor(categoryId: string): number {
    return visibleItems.filter((item) => item.category_id === categoryId).filter((item) => {
      const server = checks.find((row) => row.item_id === item.id);
      if (server?.result !== "fail" || !server.observation_id) return false;
      const observation = observations.find((row) => row.id === server.observation_id);
      return !observation?.fixed_at;
    }).length;
  }

  async function submitCategory(categoryId: string): Promise<{ ok: true } | { ok: false; error: string }> {
    const rows = visibleItems.filter((item) => item.category_id === categoryId);
    const unmarked = rows.filter((item) => {
      const result = draft.pending[item.id]?.result ?? checks.find((row) => row.item_id === item.id)?.result;
      return result !== "pass" && result !== "fail" && result !== "na";
    });
    if (unmarked.length > 0) {
      return { ok: false, error: `Mark every item before submitting this section. Still open: ${unmarked.slice(0, 4).map((item) => item.title).join(", ")}.` };
    }
    const missingNotes = rows.filter((item) => {
      if (!(failureNotesRequired || item.failure_note_required)) return false;
      const result = draft.pending[item.id]?.result ?? checks.find((row) => row.item_id === item.id)?.result;
      if (result !== "fail") return false;
      const note = (draft.pending[item.id]?.note ?? draft.notes[item.id] ?? checks.find((row) => row.item_id === item.id)?.note ?? "").trim();
      return note.length === 0;
    });
    if (missingNotes.length > 0) {
      return { ok: false, error: `Describe ${missingNotes.slice(0, 4).map((item) => item.title).join(", ")} before submitting this section.` };
    }
    const missing = rows.flatMap((item) => {
      const result = draft.pending[item.id]?.result ?? checks.find((row) => row.item_id === item.id)?.result;
      if (result !== "fail") return [];
      const local = photos.queue.some((photo) => photo.itemId === item.id && photo.kind === "fail");
      if (local) return [];
      if (!(failurePhotosRequired || item.failure_photo_required)) return [];
      const observationId = checks.find((row) => row.item_id === item.id)?.observation_id ?? null;
      if (savedFailureCount(item.id, observationId) > 0) return [];
      return [item.title || "a failed item"];
    });
    if (missing.length > 0) {
      return { ok: false, error: `Add a failure photo for ${missing.join(", ")} before submitting.` };
    }
    const flushed = await flushAll();
    if (!flushed.ok) return flushed;
    const saved = await submitQcSessionSection(sessionId, categoryId);
    if (!saved.ok) return saved;
    const submittedAt = new Date().toISOString();
    setSectionRows((current) => {
      const next = current.map((row) => (row.category_id === categoryId ? { ...row, submitted_at: submittedAt, result: saved.data.result } : row));
      if (next.some((row) => row.category_id === categoryId)) return next;
      return [...next, { category_id: categoryId, submitted_at: submittedAt, result: saved.data.result }];
    });
    setOpenSectionId((current) => (current === categoryId ? null : current));
    void Promise.resolve(onChanged());
    return { ok: true };
  }

  async function correctCategory(categoryId: string): Promise<{ ok: true } | { ok: false; error: string }> {
    const started = await startQcCorrection(sessionId, categoryId);
    if (!started.ok) return started;
    onOpenSession(started.data.session_id);
    return { ok: true };
  }

  return {
    error,
    setError,
    visibleCategories,
    chosen,
    visibleItems,
    loose,
    examples,
    categoryByObservation,
    setCategoryByObservation,
    openSectionId,
    setOpenSectionId,
    sectionRows,
    failurePhotosRequired,
    failureNotesRequired,
    draft,
    photos,
    checks,
    savedFailureCount,
    openFailuresFor,
    submitCategory,
    correctCategory,
    reload,
    onChanged,
    focusStamp: `${selected.join(",")}:${items.length}`,
    empty: !error && visibleCategories.length === 0 && loose.length === 0 && examples.length === 0,
  };
}
